'use client';

import { useCallback, useRef, useState } from 'react';

import { getOtherSiteConfig, getSiteConfig } from '../../../config/sites';
import styles from '../../../styles/components/header/siteSwitch.module.scss';

/**
 * Превключвател между двата сайта — плъзгачът се ДЪРПА с пръст/мишка и щом мине
 * отсреща, сайтът се сменя. Като „slide to unlock", не като бутон.
 *
 * КАК СЕ ЧЕТЕ (правилото е от насоките за toggle: палецът показва КЪДЕ СИ, а
 * не накъде води — виж NN/g „Toggle-Switch Guidelines"):
 *   • дръжката носи логото на ТЕКУЩИЯ сайт и стои от неговата страна → „тук съм"
 *   • полето до нея е в цвета и с името на ДРУГИЯ → „там мога да отида"
 * Така и двете са видни едновременно и няма какво да се тълкува.
 *
 * ПРИ ВЛАЧЕНЕ всичко се разменя наведнъж около средата: логото в дръжката
 * прелива към другия сайт, името също, а фонът пътува с широк градиент. Като
 * пуснеш на другия край, бутонът вече показва състоянието, в което отиваш.
 *
 * ЗАЩО ОСТАВА <a href>, а не <div role="button">: жестът е надстройка. Без
 * JavaScript, с клавиатура и с екранен четец връзката си работи по нормалния
 * начин — Enter отваря другия сайт. Ако беше div, всичко това щеше да се пише
 * наново и по-зле.
 *
 * ЗАЩО КЛИКЪТ СЕ ОТМЕНЯ СЛЕД ВЛАЧЕНЕ: при дърпане и пускане назад браузърът
 * пак праща `click`. Без проверката за изминато разстояние всяко недовършено
 * дърпане щеше да отваря другия сайт — точно обратното на намерението.
 */

const CURRENT = getSiteConfig();
const OTHER = getOtherSiteConfig();
const HREF = `https://${OTHER.domain.production}`;

// Цветовете на другия сайт не са достъпни през var(--primary-*) — те са на
// текущата тема. Мастилото идва отделно, защото контрастът върху двата акцента
// е различен: върху зеленото на Travogue върви бяло, върху златистото на
// BabyVogue — тъмно.
// Обектът е на ниво модул: сайтът идва от env при билд.
const SWITCH_VARS = {
  '--switch-bg': OTHER.theme.switchAccent,
  '--switch-bg-deep': OTHER.theme.switchAccentDeep,
  '--switch-ink': OTHER.theme.switchAccentInk,
  // Цветът на ТЕКУЩИЯ сайт — открива се зад дръжката при влачене.
  '--switch-home': CURRENT.theme.switchAccent,
  '--switch-home-deep': CURRENT.theme.switchAccentDeep,
  '--switch-home-ink': CURRENT.theme.switchAccentInk,
};

/** Колко от пътя трябва да е минала дръжката, за да се брои за превключване. */
const COMMIT_AT = 0.55;
/** Над това разместване жестът е влачене, не клик. */
const DRAG_SLOP_PX = 4;

const clamp01 = (n) => Math.min(1, Math.max(0, n));

export default function SiteSwitch({ expanded = false }) {
  const trackRef = useRef(null);
  const knobRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [dragging, setDragging] = useState(false);

  // В ref, не в state: четат се вътре в pointermove, който не бива да чака
  // повторно създаване на обработчика при всеки кадър.
  const startX = useRef(0);
  const travel = useRef(0);
  const moved = useRef(false);

  const endDrag = useCallback((commit) => {
    setDragging(false);
    if (commit) {
      setProgress(1);
      window.location.href = HREF;
    } else {
      setProgress(0);
    }
  }, []);

  const handlePointerDown = useCallback((e) => {
    if (e.button != null && e.button !== 0) {
      return;
    }
    const track = trackRef.current;
    const knob = knobRef.current;
    if (!track || !knob) {
      return;
    }
    // Пътят се мери при всяко хващане — ширината на пистата се сменя с
    // брейкпойнта, а една сметка при билд щеше да е грешна на телефон.
    travel.current = Math.max(1, track.clientWidth - knob.offsetWidth - 8);
    startX.current = e.clientX;
    moved.current = false;
    setDragging(true);
    knob.setPointerCapture?.(e.pointerId);
  }, []);

  const handlePointerMove = useCallback(
    (e) => {
      if (!dragging) {
        return;
      }
      const dx = e.clientX - startX.current;
      if (Math.abs(dx) > DRAG_SLOP_PX) {
        moved.current = true;
      }
      setProgress(clamp01(dx / travel.current));
    },
    [dragging],
  );

  const handlePointerUp = useCallback(() => {
    if (!dragging) {
      return;
    }
    endDrag(progress >= COMMIT_AT);
  }, [dragging, endDrag, progress]);

  const handleClick = useCallback((e) => {
    // Влачене, което не е стигнало до края → не е клик, не отваряме нищо.
    if (moved.current) {
      e.preventDefault();
      moved.current = false;
    }
  }, []);

  // Позицията на дръжката се подава като px, а не като процент: transform в
  // проценти се смята спрямо самата дръжка, не спрямо пътя ѝ.
  //
  // Нов обект при всяко движение — това Е механизмът: позицията се подава на CSS
  // през тези променливи. useMemo тук само би скрил факта. Заглушаването стои при
  // създаването на обекта, защото правилото сочи него, не мястото в JSX-а.
  // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
  const style = {
    ...SWITCH_VARS,
    '--switch-x': `${progress * travel.current}px`,
    '--switch-p': progress,
  };

  return (
    <a
      className={expanded ? `${styles.root} ${styles.expanded}` : styles.root}
      href={HREF}
      style={style}
      draggable={false}
      data-dragging={dragging ? 'true' : undefined}
      onClick={handleClick}
      aria-label={`Плъзни, за да отвориш ${OTHER.branding.name}`}
      title={`Плъзни, за да отвориш ${OTHER.branding.name}`}
    >
      <span className={styles.track} ref={trackRef}>
        {/* Диагонален блик + зърно: пистата да не е плосък цветен правоъгълник */}
        <span className={styles.sheen} aria-hidden="true" />
        <span className={styles.grain} aria-hidden="true" />
        {/* Двата надписа са огледални: този на текущия сайт се появява в
            откритото зад дръжката, докато този на другия избледнява. */}
        <span className={styles.labelHome} aria-hidden="true">
          {CURRENT.branding.name}
        </span>

        <span className={styles.label} aria-hidden="true">
          {OTHER.branding.name}
        </span>

        <span
          className={styles.knob}
          ref={knobRef}
          aria-hidden="true"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          {/* Двете лога се сменят заедно с имената: в покой е текущият сайт
              („тук съм"), в края — другият („това отварям").
              SVG от CDN на фиксиран размер: next/image няма какво да оптимизира
              при вектор, а добавя loader и заявка. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img className={styles.logoHome} src={CURRENT.branding.logoUrl} alt="" />
          {/* `loading="lazy"`: това лого се вижда чак в края на анимацията, но
              като `<img>` в SSR обвивката React му вдигаше `<link rel=preload>`
              ПРЕДИ този на LCP корицата (прод 2026-08-29: 3 KB пред 20 KB). */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img className={styles.logo} src={OTHER.branding.logoUrl} alt="" loading="lazy" />
        </span>
      </span>
    </a>
  );
}
